import * as THREE from 'three';
import type { Entity, EntityCtx } from './Entity';
import { Bullet } from './entities/Bullet';
import { PlayerPlane } from './entities/PlayerPlane';
import { EnemyPlane } from './entities/EnemyPlane';
import { Zeppelin } from './entities/Zeppelin';
import { Explosion } from './entities/Explosion';
import type { Building } from './Scene';
import { useStore } from '../state/store';

/** How close an enemy bullet must pass to the player to count as a near-miss (combo). */
const NEAR_MISS_DIST = 14;
/** Heavy shells (impactScale > 1) splash half damage to other valid targets within this radius. */
const SHELL_SPLASH_RADIUS = 22;

let buildingHit: Building | null = null;

/** Cheap swept-sphere test for bullets against a stationary sphere. Returns t in [0,1]. */
function sweepBullet(p0: THREE.Vector3, p1: THREE.Vector3, center: THREE.Vector3, radius: number): number {
  const dx = p1.x - p0.x, dy = p1.y - p0.y, dz = p1.z - p0.z;
  const fx = p0.x - center.x, fy = p0.y - center.y, fz = p0.z - center.z;
  const a = dx * dx + dy * dy + dz * dz;
  if (a < 1e-9) return -1;
  const b = 2 * (fx * dx + fy * dy + fz * dz);
  const c = fx * fx + fy * fy + fz * fz - radius * radius;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return -1;
  const sq = Math.sqrt(disc);
  const t1 = (-b - sq) / (2 * a);
  if (t1 >= 0 && t1 <= 1) return t1;
  const t2 = (-b + sq) / (2 * a);
  if (t2 >= 0 && t2 <= 1) return t2;
  return -1;
}

/** Multiplier on target bounds for arcade forgiveness. 1.0 = exact OBB; 1.15 = small grace. */
const HIT_INFLATE = 1.15;
/** Same idea for plane-vs-plane ram (still a fast cheap sphere check). */
const RAM_INFLATE = 1.4;

const _invMat = new THREE.Matrix4();
const _localP0 = new THREE.Vector3();
const _localP1 = new THREE.Vector3();
const _bMin = new THREE.Vector3();
const _bMax = new THREE.Vector3();
// Hit-resolution scratch — Explosion copies the position and setDrift clones, so sharing these
// across hits is safe and keeps the per-bullet hit path allocation-free.
const _hitPos = new THREE.Vector3();
const _DRIFT_8 = new THREE.Vector3(0, 8, 0);
const _DRIFT_9 = new THREE.Vector3(0, 9, 0);

/**
 * Liang–Barsky / slab-method segment-vs-AABB. Returns the smallest t in [0,1] at which
 * the segment (p0 → p1) enters the box, or -1 if it doesn't intersect.
 */
function sweepSegmentAABB(p0: THREE.Vector3, p1: THREE.Vector3, min: THREE.Vector3, max: THREE.Vector3): number {
  let tmin = 0, tmax = 1;
  for (let i = 0; i < 3; i++) {
    const p0i = i === 0 ? p0.x : i === 1 ? p0.y : p0.z;
    const p1i = i === 0 ? p1.x : i === 1 ? p1.y : p1.z;
    const mn = i === 0 ? min.x : i === 1 ? min.y : min.z;
    const mx = i === 0 ? max.x : i === 1 ? max.y : max.z;
    const di = p1i - p0i;
    if (Math.abs(di) < 1e-9) {
      if (p0i < mn || p0i > mx) return -1; // parallel and outside this slab
    } else {
      let t1 = (mn - p0i) / di;
      let t2 = (mx - p0i) / di;
      if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
      if (t1 > tmin) tmin = t1;
      if (t2 < tmax) tmax = t2;
      if (tmin > tmax) return -1;
    }
  }
  return tmin; // first intersection (could be 0 if p0 is already inside)
}

/**
 * Test a bullet's swept segment against a target. Returns `{ t, partId? }` for the first
 * intersection or `{ t: -1 }` if none. `partId` is set when the target uses `hitParts`
 * multi-OBB collision (e.g. SkyCarrier's 4 pods + deck).
 */
function bulletHits(bullet: Bullet, target: Entity): { t: number; partId?: string } {
  // Multi-part: iterate each part, find earliest hit.
  if (target.hitParts && target.hitParts.length) {
    let bestT = 2;
    let bestId: string | undefined;
    for (const part of target.hitParts) {
      part.object.updateWorldMatrix(true, false);
      _invMat.copy(part.object.matrixWorld).invert();
      _localP0.copy(bullet.prevPosition).applyMatrix4(_invMat);
      _localP1.copy(bullet.group.position).applyMatrix4(_invMat);
      _bMin.copy(part.box.min).multiplyScalar(HIT_INFLATE);
      _bMax.copy(part.box.max).multiplyScalar(HIT_INFLATE);
      const t = sweepSegmentAABB(_localP0, _localP1, _bMin, _bMax);
      if (t >= 0 && t < bestT) { bestT = t; bestId = part.id; }
    }
    return bestT < 2 ? { t: bestT, partId: bestId } : { t: -1 };
  }
  // Single-OBB: existing path.
  if (target.localBounds) {
    target.group.updateWorldMatrix(true, false);
    _invMat.copy(target.group.matrixWorld).invert();
    _localP0.copy(bullet.prevPosition).applyMatrix4(_invMat);
    _localP1.copy(bullet.group.position).applyMatrix4(_invMat);
    _bMin.copy(target.localBounds.min).multiplyScalar(HIT_INFLATE);
    _bMax.copy(target.localBounds.max).multiplyScalar(HIT_INFLATE);
    return { t: sweepSegmentAABB(_localP0, _localP1, _bMin, _bMax) };
  }
  // Sphere fallback (sampled along the segment).
  const r = (bullet.radius + target.radius) * HIT_INFLATE;
  const r2 = r * r;
  const STEPS = 4;
  for (let s = 0; s <= STEPS; s++) {
    const t = s / STEPS;
    const x = bullet.prevPosition.x + (bullet.group.position.x - bullet.prevPosition.x) * t;
    const y = bullet.prevPosition.y + (bullet.group.position.y - bullet.prevPosition.y) * t;
    const z = bullet.prevPosition.z + (bullet.group.position.z - bullet.prevPosition.z) * t;
    const dx = x - target.group.position.x;
    const dy = y - target.group.position.y;
    const dz = z - target.group.position.z;
    if (dx * dx + dy * dy + dz * dz < r2) return { t };
  }
  return { t: -1 };
}

export function runCollisions(entities: Entity[], ctx: EntityCtx) {
  const bullets: Bullet[] = [];
  const players: PlayerPlane[] = [];
  const enemies: EnemyPlane[] = [];
  const zeppelins: Zeppelin[] = [];
  const allyGround: Entity[] = []; // friendly ground units (town gunners, allied tanks)
  const enemyTanks: Entity[] = []; // hostile tanks — what player-side bullets strafe

  for (const e of entities) {
    if (e.dead) continue;
    if (e.kind === 'bullet') bullets.push(e as Bullet);
    else if (e.kind === 'player') players.push(e as PlayerPlane);
    else if (e.kind === 'enemy') enemies.push(e as EnemyPlane);
    else if (e.kind === 'zeppelin') zeppelins.push(e as Zeppelin);
    else if (e.kind === 'ally') allyGround.push(e);
    else if (e.kind === 'tank') enemyTanks.push(e);
  }

  // Friendly targets for enemy fire = all controllable allies + the player's planes.
  const friendlies: Entity[] = [...players, ...allyGround];

  // Bullets vs targets — swept-segment-vs-OBB so a fast bullet can't tunnel through.
  for (const b of bullets) {
    if (b.dead) continue;
    // Player rounds hit enemies; rounds you personally fired (friendlyFire) ALSO hit friendlies.
    const candidates: Entity[] = b.owner === 'player'
      ? (b.friendlyFire ? [...enemies, ...zeppelins, ...enemyTanks, ...friendlies] : [...enemies, ...zeppelins, ...enemyTanks])
      : friendlies;
    let bestT = 2; // > 1 = "no hit yet"
    let bestTarget: Entity | null = null;
    let bestPartId: string | undefined;
    for (const t of candidates) {
      if (t.dead) continue;
      if (!t.alive()) continue; // pass through wreckage
      if (t.id === b.ownerId) continue;
      const ht = bulletHits(b, t);
      if (ht.t >= 0 && ht.t < bestT) {
        bestT = ht.t;
        bestTarget = t;
        bestPartId = ht.partId;
      }
    }
    // Rounds you personally fired can also strafe friendly village buildings (friendly fire), and
    // heavy artillery rounds (enemy tank shells, flagged hitsBuildings) crater them too. AI MG
    // rounds (neither flag) leave the villages alone.
    if ((b.owner === 'player' && b.friendlyFire) || b.hitsBuildings) {
      for (const v of ctx.game.gameScene.villages) {
        if (v.destroyed) continue;
        for (const bld of v.buildings) {
          if (bld.destroyed) continue;
          // Cheap swept sphere from prevPosition → position vs the building's bounding sphere.
          const t = sweepBullet(b.prevPosition, b.group.position, bld.position, bld.radius);
          if (t >= 0 && t < bestT) {
            bestT = t;
            bestTarget = null;       // building hits don't go through `Entity.damage`
            buildingHit = bld;
          }
        }
      }
    }
    if (bestTarget) {
      // ARMOR-PIERCING: heavy shells deal triple damage to GROUND-boss parts (the same gates —
      // shields, vents, windows — still apply). Tanks aren't required to kill a boss, but armor
      // pushed onto one meaningfully accelerates its demise.
      let dmg = b.damageAmount;
      if (b.impactScale > 1 && bestTarget.isBoss && bestTarget.kind === 'tank' && b.owner === 'player') {
        dmg *= 3;
        ctx.game.armorPiercingHint();
      }
      if (bestPartId) bestTarget.damagePart(bestPartId, dmg, ctx);
      else bestTarget.damage(dmg, ctx);
      // Stamp friendlies so the threat-alert system can flag who's under fire.
      if (bestTarget.friendly) bestTarget.lastDamageAt = ctx.now;
      const hitPos = _hitPos.lerpVectors(b.prevPosition, b.group.position, Math.max(0, bestT));
      if (b.impactScale > 1) {
        // Heavy projectile (tank shell) — a real detonation, not a spark.
        new Explosion(hitPos, 4 * b.impactScale, 0.7, 'fire').addTo(ctx);
        new Explosion(hitPos, 3 * b.impactScale, 2.5, 'smoke').setDrift(_DRIFT_8).addTo(ctx);
        ctx.game.audio.playBoom(0.6);
        // Shell burst SPLASHES: half damage to the bullet's other valid targets near the impact —
        // a tank round landing in a column hurts the column. (Same candidate set as the direct
        // hit, so the friendly-fire rules carry over unchanged.)
        for (const t of candidates) {
          if (t === bestTarget || t.dead || !t.alive() || t.id === b.ownerId) continue;
          if (t.position.distanceTo(hitPos) <= SHELL_SPLASH_RADIUS) {
            t.damage(Math.max(1, Math.round(b.damageAmount / 2)), ctx);
            if (t.friendly) t.lastDamageAt = ctx.now;
          }
        }
      } else {
        new Explosion(hitPos, 4, 0.25).addTo(ctx);
      }
      b.dead = true;
    } else if (buildingHit) {
      const hitPos = _hitPos.lerpVectors(b.prevPosition, b.group.position, Math.max(0, bestT));
      buildingHit.destroy(ctx.game.gameScene.root);
      // Bigger pyrotechnic for a destroyed building.
      new Explosion(hitPos, 30, 0.9, 'fire').addTo(ctx);
      new Explosion(hitPos, 22, 3, 'smoke').setDrift(_DRIFT_9).addTo(ctx);
      ctx.game.audio.playBoom(0.6);
      b.dead = true;
    }
    // reset the per-iteration var for the next bullet
    buildingHit = null;
  }

  // Plane-vs-plane ram — still cheap sphere check (rare, low-stakes vs the bullet path).
  for (const p of players) {
    for (const e of enemies) {
      if (e.dead) continue;
      if (p.position.distanceTo(e.position) < (p.radius + e.radius) * RAM_INFLATE) {
        p.damage(2, ctx);
        e.damage(3, ctx);
        p.lastDamageAt = ctx.now;
      }
    }
  }

  // Near-miss combo: only the unit the player is actually controlling builds combo when
  // enemy fire whips past. Counted once per bullet via the `nearMissed` flag.
  const player = ctx.game.controlled;
  if (player && player.alive() && !player.dead) {
    for (const b of bullets) {
      if (b.dead || b.owner !== 'enemy' || b.nearMissed) continue;
      const d = b.group.position.distanceTo(player.position);
      if (d < NEAR_MISS_DIST) {
        b.nearMissed = true;
        useStore.getState().registerNearMiss();
      }
    }
  }
}
